import { useState } from 'react';
import { Button, Field, Icon, Input } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import { AuthShell } from './AuthShell.tsx';

/**
 * "I cannot get into the console."
 *
 * ── WHY THE ANSWER IS ALWAYS THE SAME ────────────────────────────────────
 *
 * This screen has ONE success state and it does not depend on whether the
 * address exists. The route answers identically for a known address, an
 * unknown one, a suspended account and an account with no confirmed second
 * factor — in the body, the status and the elapsed time — so a screen that
 * branched on the answer would be inventing a distinction the API refuses to
 * make, and re-opening the enumeration hole the route closed.
 *
 * So: no "we couldn't find that address", no "check your spelling", no green
 * tick that means anything more than "the request was received".
 *
 * ── AND WHY IT SAYS WHAT A LINK CANNOT DO ────────────────────────────────
 *
 * Two things a locked-out administrator needs to know BEFORE they go and wait
 * for mail, both of which this screen is the last chance to say:
 *
 *   1. The link sets a password and nothing else. The second factor still
 *      applies. Somebody who has lost their authenticator too is not getting
 *      back in this way, and should stop waiting and go to a Super Admin.
 *   2. An account that has never confirmed a second factor gets no link at
 *      all. That is deliberate — on such an account a password alone reaches
 *      the enrolment step, which mints a full session, so a link in an inbox
 *      would be the whole console. Stated as a condition rather than as a
 *      result, because telling somebody "your account has no second factor"
 *      in response to their address would be exactly the probe the route
 *      refuses to be.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<'ENQUEUED' | 'SUPPRESSED' | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || email.trim() === '') return;
    setBusy(true);
    setFailure(null);
    try {
      const answer = await api.post<{ mailDelivery: 'ENQUEUED' | 'SUPPRESSED' }>(
        '/api/auth/password-reset/request',
        { email: email.trim() },
      );
      setSent(answer.mailDelivery);
    } catch (error) {
      /*
       * Only a TRANSPORT failure reaches here — the route answers 200 on every
       * branch. So this says the request did not arrive, which is a different
       * claim from anything about the address, and must not be worded as one.
       */
      setFailure(
        error instanceof ApiRequestError
          ? error.message
          : 'The request did not reach the platform. Nothing was sent. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (sent !== null) {
    return (
      <AuthShell>
      <div className="stack signin-step">
        <h3>Check your email</h3>
        <p className="text-subtle">
          If <strong>{email.trim()}</strong> belongs to an account that can be recovered this way,
          a link is on its way. It works once and expires in an hour.
        </p>

        {/* The one honest thing this screen knows about delivery, and it is a
            property of the DEPLOYMENT rather than of the address — so saying it
            leaks nothing. Without it, a developer on an environment that sends
            no mail waits for something that was never coming. */}
        {sent === 'SUPPRESSED' && (
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              This environment does not deliver mail to that address. The message has been
              recorded and will not arrive. On a developer machine, read it out of the outbox.
            </div>
          </div>
        )}

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            <p>
              <strong>You will still need your authenticator.</strong> The link sets a password
              and nothing else — the console will still ask for a code.
            </p>
            <p>
              If you have lost that too, this will not get you back in. Ask a Super Admin to reset
              your second factor; it is the only thing that can.
            </p>
          </div>
        </div>

        <Button onClick={() => (window.location.href = '/')}>Back to sign in</Button>
      </div>
    </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form className="stack signin-step" onSubmit={(e) => void submit(e)} noValidate>
      <h3>Reset your password</h3>
      <p className="text-subtle">
        Type the address you sign in with. If it belongs to an account that can be recovered this
        way, we will email a link that works once.
      </p>

      <Field label="Work email">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            type="email"
            autoComplete="username"
            value={email}
            placeholder="name@dayonops.ph"
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </Field>

      {failure !== null && (
        <div className="notice notice-danger">
          <Icon name="warning" />
          <div>{failure}</div>
        </div>
      )}

      <Button variant="primary" block type="submit" busy={busy} disabled={email.trim() === ''}>
        Email me a link
        <Icon name="arrow-right" />
      </Button>

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          <p>
            A reset link sets a password only. <strong>Your second factor still applies</strong>,
            and the console will ask for a code when you sign in.
          </p>
          <p>
            Accounts that have never confirmed a second factor cannot be recovered this way at
            all — on those, a password alone would be enough to get in, which is exactly what a
            link in an inbox must not become. Ask a Super Admin instead.
          </p>
        </div>
      </div>

      <div className="signin-policy text-subtle">
        <a href="/">Back to sign in</a>
      </div>
      </form>
    </AuthShell>
  );
}
